import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../singleton/axios';

export interface PriceRecordEod {
  date: string;
  open: number;
  low: number;
  high: number;
  close: number;
  volume: number;
}

export interface PriceDataResponse {
  data: PriceRecordEod[];
  metadata: {
    requestedFrom: string;
    requestedTo: string;
    recordCount: number;
  };
}

export interface PriceDataWithMetadata {
  data: PriceRecordEod[];
  requestedFrom: string;
  requestedTo: string;
  recordCount: number;
}

/**
 * Hook for fetching end-of-day price data.
 * Used for 5Y and 10Y chart views.
 *
 * @param ticker - Stock ticker symbol
 * @param from - Start date (YYYY-MM-DD)
 * @param to - End date (YYYY-MM-DD)
 * @param enabled - Set false to keep the query mounted but never fetch
 */
export const useCompanyPriceEod = (
  ticker: string | undefined,
  from: string,
  to: string,
  enabled: boolean = true
) => {
  return useQuery<PriceDataWithMetadata, Error>({
    queryKey: ['companyPriceEod', ticker, from, to],
    queryFn: async () => {
      if (!ticker) {
        throw new Error('Ticker is required');
      }

      const params = {
        ticker,
        from,
        to,
      };

      const { data: response } = await api.get<PriceDataResponse>('/company-price-eod', {
        params,
      });

      // Transform the response to a flatter structure for easier consumption
      return {
        data: response.data,
        requestedFrom: response.metadata.requestedFrom,
        requestedTo: response.metadata.requestedTo,
        recordCount: response.metadata.recordCount,
      };
    },
    enabled: enabled && !!ticker,
    refetchOnWindowFocus: false,
    staleTime: 60 * 60 * 1000, // 1 hour for EOD data
  });
};
